import appFeatures from '../common/app-features'
import axios from 'axios'
import exceptionHandler from '@/common/exception-handler'

const getAccessibleCompanies = () => {
  return axios.get('/api/companies/accessible')
    .then(response => response.data)
    .catch(error => exceptionHandler.parse(error))
}

const getNavigationLeft = (enabledFeatures) => {
  return filterNavigation(navigationLinks.leftNavigation, enabledFeatures)
}

const getNavigationTop = (enabledFeatures) => {
  return filterNavigation(navigationLinks.topNavigation, enabledFeatures)
}

const hasAllFeatures = (features, required) => {
  return Array.isArray(required)
    ? required.every(value => features.includes(value))
    : features.find(feature => feature === required)
}

const filterNavigation = (navigation, enabledFeatures) => {
  let filteredNavigation = []

  if (enabledFeatures.length === 0) {
    return filteredNavigation
  }

  navigation.forEach(group => {
    if (group.hasOwnProperty('name')) {
      if (hasAllFeatures(enabledFeatures, group.feature)) {
        filteredNavigation.push(group)
      }
    } else {
      let filteredChildren = []

      group.childRoutes
        .forEach(route => {
          if (route.public || hasAllFeatures(enabledFeatures, route.feature)) {
            filteredChildren.push(route)
          }
        })

      if (filteredChildren.length > 0) {
        filteredNavigation.push({ ...group, childRoutes: filteredChildren })
      }
    }
  })

  return filteredNavigation
}

const navigationLinks = {
  leftNavigation: [
    {
      id: 'home',
      displayText: 'Home',
      icon: 'fa-home',
      childRoutes: [
        {
          id: 'home',
          displayText: 'Home',
          name: 'home',
          public: true
        }
      ]
    },
    {
      id: 'analytics',
      displayText: 'Analytics',
      icon: 'fa-analytics',
      childRoutes: [
        {
          id: 'storeSummary',
          displayText: 'Store Summary',
          name: 'storeSummary',
          feature: appFeatures.dashboard
        },
        {
          id: 'productMix',
          displayText: 'Product Mix',
          name: 'productMix',
          feature: appFeatures.dashboard
        },
        {
          id: 'serverPerformance',
          displayText: 'Server Performance',
          name: 'serverPerformance',
          feature: appFeatures.dashboard
        },
        {
          id: 'deliveries',
          displayText: 'Deliveries',
          name: 'deliveries',
          feature: appFeatures.dashboard
        },
        {
          id: 'employeePayroll',
          displayText: 'Employee Payroll',
          name: 'employeePayroll',
          feature: appFeatures.dashboard
        },
        {
          id: 'allReports',
          displayText: 'All Reports',
          name: 'allReports',
          feature: appFeatures.dashboard
        },
        {
          id: 'ticketFinderSearch',
          displayText: 'Ticket Finder',
          name: 'ticketFinderSearch',
          feature: appFeatures.dashboard
        }
      ]
    },
    {
      id: 'accounts',
      displayText: 'Accounts',
      icon: 'fa-user-shield',
      childRoutes: [
        {
          id: 'companies',
          feature: appFeatures.editCompanies,
          displayText: 'Companies',
          name: 'companiesList'
        },
        {
          id: 'locations',
          displayText: 'Locations',
          name: 'locationsList',
          public: true
        },
        {
          id: 'groups',
          displayText: 'Groups',
          name: 'groupsList',
          public: true
        }
      ]
    },
    {
      id: 'employment',
      displayText: 'Employment',
      icon: 'fa-person-dolly',
      childRoutes: [
        {
          id: 'employmentAdmin',
          feature: appFeatures.manageMenus,
          displayText: 'Employment Admin ',
          name: 'employmentAdmin'
        },
        {
          id: 'employees',
          feature: appFeatures.userAdmin,
          displayText: 'Employees',
          name: 'employees'
        },
        {
          id: 'userRoles',
          feature: appFeatures.userRolesAdmin,
          displayText: 'Security Roles',
          name: 'RolePermissions',
          public: true
        },
        {
          id: 'defaultRoles',
          feature: appFeatures.defaultRoleAdmin,
          displayText: 'Default Security Roles',
          name: 'DefaultRolePermissions'
        },
        {
          id: 'employment',
          displayText: 'Employment',
          name: 'employment',
          feature: appFeatures.accessConfigurationOptions
        }
      ]
    },
    {
      id: 'customer',
      displayText: 'Customer',
      icon: 'fa-user',
      childRoutes: [
        {
          id: 'pushNotifications',
          displayText: 'Push Notifications',
          name: 'pushNotifications',
          feature: appFeatures.manageOnline
        }
      ]
    },
    {
      id: 'configuration',
      displayText: 'Configuration',
      icon: 'fa-cog',
      childRoutes: [
        {
          id: 'limitedManageOnline',
          displayText: 'Thrive Online',
          name: 'limitedManageOnlineForm',
          feature: appFeatures.limitedManageOnline
        },
        {
          id: 'hours',
          displayText: 'Hours',
          name: 'hoursForm',
          feature: appFeatures.accessConfigurationOptions
        },
        {
          id: 'orderTypes',
          displayText: 'Order Types',
          name: 'orderTypes',
          feature: appFeatures.accessConfigurationOptions
        },
        {
          id: 'deliverySettings',
          displayText: 'Delivery',
          name: 'deliverySettingsForm',
          feature: appFeatures.locationSettings
        },
        {
          id: 'tableAndBar',
          displayText: 'Table & Bar',
          name: 'tableAndBar',
          feature: appFeatures.accessConfigurationOptions
        },
        {
          id: 'onlineDesign',
          feature: appFeatures.manageOnline,
          displayText: 'Online Design',
          name: 'onlineDesign'
        },
        {
          id: 'posDesign',
          feature: appFeatures.accessConfigurationOptions,
          displayText: 'POS Design',
          name: 'posDesign'
        },
        {
          id: 'marketing',
          feature: appFeatures.accessConfigurationOptions,
          displayText: 'Marketing',
          name: 'marketing'
        },
        {
          id: 'payments',
          displayText: 'Payments',
          name: 'payments',
          feature: appFeatures.accessConfigurationOptions
        },
        {
          id: 'creditAndGift',
          displayText: 'Credit & Gift',
          name: 'creditAndGift',
          feature: appFeatures.configureCreditCards
        },
        {
          id: 'mobileApp',
          displayText: 'Mobile App',
          name: 'mobileApp',
          feature: appFeatures.accessConfigurationOptions
        }
      ]
    },
    {
      id: 'hardware',
      displayText: 'Hardware',
      icon: 'fa-wrench',
      childRoutes: [
        {
          id: 'stations',
          feature: appFeatures.configureHardware,
          displayText: 'Stations',
          name: 'stations'
        },
        {
          id: 'printers',
          feature: appFeatures.configureHardware,
          displayText: 'Printers',
          name: 'printers'
        },
        {
          id: 'printerOptions',
          feature: appFeatures.configureHardware,
          displayText: 'Printer Options',
          name: 'printerOptions'
        },
        {
          id: 'cashDrawers',
          feature: appFeatures.configureHardware,
          displayText: 'Cash Drawers',
          name: 'cashDrawers'
        }
      ]
    },
    {
      id: 'kiosk',
      displayText: 'Kiosk',
      icon: 'fa-window',
      childRoutes: [
        {
          id: 'kioskMenu',
          feature: appFeatures.manageMenus,
          displayText: 'Kiosk Menu',
          name: 'kioskMenu'
        },
        {
          id: 'kioskDesign',
          feature: appFeatures.accessConfigurationOptions,
          displayText: 'Kiosk Design',
          name: 'kioskDesign'
        }
      ]
    },
    {
      id: 'menu',
      displayText: 'Menu',
      icon: 'fa-utensils',
      childRoutes: [
        {
          id: 'menusAdmin',
          feature: appFeatures.manageMenus,
          displayText: 'Menus Admin ',
          name: 'menusAdmin'
        },
        {
          id: 'menuItems',
          feature: appFeatures.manageMenus,
          displayText: 'Menu Items',
          name: 'menuItems'
        },
        {
          id: 'taxStructures',
          feature: appFeatures.manageMenus,
          displayText: 'Tax Structures',
          name: 'taxStructures'
        },
        {
          id: 'menuPrinters',
          feature: appFeatures.manageMenus,
          displayText: 'Menu Printers',
          name: 'menuPrinters'
        },
        {
          id: 'kps',
          feature: appFeatures.manageMenus,
          displayText: 'KPS',
          name: 'kps'
        },
        {
          id: 'pricingSchemes',
          feature: appFeatures.manageMenus,
          displayText: 'Pricing Schemes',
          name: 'pricingSchemes'
        },
        {
          id: 'modifiers',
          feature: appFeatures.manageMenus,
          displayText: 'Modifiers',
          name: 'modifiersList'
        },
        {
          id: 'messages',
          feature: appFeatures.manageMenus,
          displayText: 'Messages',
          name: 'menuMessages'
        },
        {
          id: 'requirements',
          feature: appFeatures.manageMenus,
          displayText: 'Requirements',
          name: 'menuRequirements'
        },
        {
          id: 'offers',
          feature: appFeatures.manageMenus,
          displayText: 'Offers',
          name: 'offersList'
        },
        {
          id: 'layout',
          feature: appFeatures.manageMenus,
          displayText: 'Menu Layout',
          name: 'menuLayout'
        },
        {
          id: 'onlineCategories',
          feature: appFeatures.manageMenus,
          displayText: 'Online Layout',
          name: 'onlineCategories'
        },
        {
          id: 'imageManager',
          feature: appFeatures.accessConfigurationOptions,
          displayText: 'Image Manager',
          name: 'imageManager'
        }
      ]
    }
  ],
  topNavigation: []
}

export default {
  getNavigationLeft,
  getNavigationTop,
  getAccessibleCompanies
}
